import {
  CustomNode,
  NodeStruct,
  NodeCategory,
  NodeParam,
} from '@/types/node-types';

/** User-visible label from struct: trimmed `user_config_name`, else `display_name`. */
export function getStructDisplayLabel(
  struct: { user_config_name?: string; display_name?: string } | undefined,
): string | undefined {
  if (!struct) return undefined;
  const custom = struct.user_config_name;
  const template = struct.display_name;
  if (typeof custom === 'string' && custom.trim()) return custom.trim();
  if (typeof template === 'string' && template) return template;
  return undefined;
}

/**
 * Node IDs that have name fields which should be pre-populated with the display_name.
 * These nodes have required name fields that benefit from being pre-filled.
 */
const NODE_NAME_IDS = [
  'evals_node', // Evaluations - has test_name field
  'text_redaction_node', // Text Redaction - has redaction_name field
  'pdf_redaction_node', // PDF Redaction - has redaction_name field
];

/**
 * Categories that support automatic name generation.
 * - AGENT: Agent nodes that need unique names (uses standard 'name' field)
 */
export const AUTO_NAME_CATEGORIES = [NodeCategory.AGENT];

/**
 * Specific field_ids for nodes that should receive auto-generated names.
 * These are the exact field names used by Evaluations and Redaction nodes.
 */
const GOVERNANCE_NAME_FIELDS = ['test_name', 'redaction_name'];

/**
 * Checks if a parameter is a "name" field that should be pre-populated.
 * This includes:
 * - Fields with field_id === 'name' (standard name field for AGENT nodes)
 * - Specific fields for targeted nodes: 'test_name' (Evaluations), 'redaction_name' (PDF/Text Redaction)
 *
 * @param param - The node parameter to check
 * @param nodeStruct - The node structure containing node_id and node_type
 * @returns true if this parameter should be pre-populated with a name
 */
export const isNameParameter = (
  param: NodeParam,
  nodeStruct: NodeStruct,
): boolean => {
  // Standard "name" field for AGENT category nodes
  if (
    param.field_id === 'name' &&
    nodeStruct.node_type === NodeCategory.AGENT
  ) {
    return true;
  }

  // For specific targeted nodes (Evaluations, Text Redaction, PDF Redaction),
  // check for their specific name fields
  if (
    NODE_NAME_IDS.includes(nodeStruct.node_id) &&
    GOVERNANCE_NAME_FIELDS.includes(param.field_id)
  ) {
    return true;
  }

  return false;
};

/**
 * Checks if a node should receive an auto-generated numbered name.
 * Only AGENT nodes get numbered names (e.g., "Agent 1", "Agent 2").
 *
 * @param nodeStruct - The node structure to check
 * @returns true if this node type supports auto-naming with numbers
 */
export const shouldAutoNameNode = (nodeStruct: NodeStruct): boolean => {
  return AUTO_NAME_CATEGORIES.includes(nodeStruct.node_type);
};

/**
 * Generates an auto-incrementing name for AGENT nodes.
 * Only applies to AGENT category nodes - they get numbered names like "Agent 1", "Agent 2".
 *
 * For other nodes with name fields (Evaluations, Text Redaction, PDF Redaction),
 * the display_name is used directly without numbering.
 *
 * @param nodeStruct - The node structure containing display_name and node_type
 * @param existingNodes - Array of existing nodes to check for name conflicts
 * @returns Auto-generated name (e.g., "Agent 1", "Agent 2") or undefined if auto-naming doesn't apply
 *
 * @example
 * // If canvas has "Agent 1" and "Agent 2", returns "Agent 3"
 * generateAutoNodeName(agentStruct, existingNodes) // => "Agent 3"
 */
export const generateAutoNodeName = (
  nodeStruct: NodeStruct,
  existingNodes: CustomNode[],
): string | undefined => {
  if (!shouldAutoNameNode(nodeStruct)) {
    return undefined;
  }

  // Find all existing nodes of the same display_name.
  // Optional chaining guards against nodes without a struct (e.g. artifact
  // nodes), which would otherwise throw when read here.
  const sameTypeNodes = existingNodes.filter(
    (node) => node.data?.struct?.display_name === nodeStruct.display_name,
  );

  // Extract numbers from existing names
  const existingNumbers: number[] = [];
  const baseNamePattern = new RegExp(`^${nodeStruct.display_name}\\s+(\\d+)$`);

  sameTypeNodes.forEach((node) => {
    const name =
      node.data.struct.user_config_name || node.data.struct.display_name;
    const match = name.match(baseNamePattern);
    if (match) {
      existingNumbers.push(parseInt(match[1], 10));
    }
  });

  // Find the next available number
  const nextNumber =
    existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

  return `${nodeStruct.display_name} ${nextNumber}`;
};
